<template>
  <label
    :for="internalId"
    :data-sb-automation="$attrs['data-sb-automation'] || 'rb-container'"
    :class="radioControlClass"
    data-sb-regression="sb-radio-button"
  >
    <i
      aria-hidden="true"
      :class="controlIndicatorClass"
    />
    <RadioButton
      :model-value="model"
      :input-id="internalId"
      :disabled="disabled"
      v-bind="$attrs"
      :value="value"
      @update:model-value="inputChanged"
    />
    <div :class="labelWwrapperClass">
      <!--
                @slot slot to show buttons to various pages
            -->
      <slot />
    </div>
  </label>
</template>

<script setup>
/* eslint-disable css-modules/no-undef-class */
import RadioButton from 'primevue/radiobutton';
import { computed, ref, defineProps, defineEmits, defineOptions, useCssModule } from 'vue';
import uuid from '@src/utils/uuid';
import { isEqual } from 'lodash';

const $style = useCssModule();

defineOptions({
  name: 'SbRadioButtonNext',
});

const props = defineProps({
  /**
     * Prop that v-model's value is bound to. This enables you to use the radio-button with
     * the v-model="myModel" syntax.
        */
  model: {
    type: [String, Boolean, Object, Number],
    default: null,
  },
  /**
       * The value of the radio-button. When selected v-model="myModel" will equal the value of this prop.
       */
  value: {
    type: [String, Boolean, Object, Number],
    default: null,
  },
  /**
       * Style variation.
       * `primary | bigbtn`
       */
  variation: {
    type: String,
    default: 'primary',
    validator: (value) => value.match(/(primary|bigbtn)/),
  },
  /**
       * Id of the radio button. If not provided the default will be the components uuid.
       */
  id: {
    type: String,
    default: null,
  },
  /**
       * Disable radio button from user input
      */
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:model']);

function inputChanged() {
  if (props.disabled) return;
  emit('update:model', props.value);
}

const internalId = ref(props.id || uuid());

const isChecked = computed(() => {
  if (Object.hasOwn(props, 'value')) return isEqual(props.model, props.value);
  return !!props.model;
});

const controlIndicatorClass = computed(() => {
  const baseVariationClass = `controlIndicator-${props.variation}`;
  const map = {
    checked: {
      primary: [$style.controlIndicator, $style[baseVariationClass], props.disabled && $style[`${baseVariationClass}-disabled`], $style[`${baseVariationClass}-checked`]],
      bigbtn: [$style.controlIndicator, $style['controlIndicator-bigbtn'], !props.disabled && $style.sbIconCheck, !props.disabled && 'sb-icon-check'],
    },
    default: {
      primary: [$style.controlIndicator, $style[baseVariationClass], props.disabled && $style[`${baseVariationClass}-disabled`]],
      bigbtn: [$style.controlIndicator, $style[`${baseVariationClass}-bigbtn`]],
    },
  };
  return map[isChecked.value ? 'checked' : 'default'][props.variation]?.filter(Boolean);
});

const labelWwrapperClass = computed(() => {
  return [$style.labelWrapper, $style[`labelWrapper-${props.variation}`]];
});

const radioControlClass = computed(() => {
  const baseClass = `sbRadioButton-${props.variation}`;
  const classes = [$style.sbRadioButton, $style[baseClass]];
  if (isChecked.value) classes.push($style[`${baseClass}-checked`]);
  if (props.disabled) classes.push($style[`${baseClass}-disabled`]);
  return classes;
});

</script>
<style lang="scss" module>
@use '@src/components/RadioButton/style/sb-radio-button.module.scss';
</style>
